import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

function Sidebar({ activeSection, setActiveSection, isMobileMenuOpen, setIsMobileMenuOpen }) {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState('');
  const [collapsed, setCollapsed] = useState(false);

  // Obtener el rol del usuario del localStorage o sessionStorage
  useEffect(() => {
    let role = '';
    let savedCollapsed = false;
    try {
      role = localStorage.getItem('userRole') || sessionStorage.getItem('userRole') || '';
      savedCollapsed = localStorage.getItem('sidebarCollapsed') === 'true';
    } catch (_) {
      try { role = sessionStorage.getItem('userRole') || ''; } catch (_) {}
    }
    setUserRole(role);
    setCollapsed(savedCollapsed);
  }, []);

  useEffect(() => {
    try { localStorage.setItem('sidebarCollapsed', collapsed); } catch (_) {}
  }, [collapsed]);
  const isDev = process.env.NODE_ENV === 'development';

  const sections = [
    { id: 'dashboard', name: 'Dashboard', icon: 'home', url: '/dashboard', roles: ['Admin', 'Recaudador', 'Auditor', 'Financiero'] },
    { id: 'zonificacion', name: 'Zonificación', icon: 'map', url: '/zonificacion', roles: ['Admin', 'Recaudador', 'Auditor', 'Monitoreo', 'Financiero'] },
    { id: 'ticket-search', name: 'Consultar Tickets', icon: 'search', url: '/ticket-search', roles: ['Admin', 'Supervisor', 'Recaudador', 'Auditor', 'Monitoreo', 'Financiero'] },
    { id: 'venta-ca', name: 'Venta CA', icon: 'search', url: '/venta-ca', roles: ['Admin', 'Supervisor'] },
    { id: 'vendors', name: 'Controladores', icon: 'users', url: '/vendors', roles: ['Admin', 'Supervisor', 'Recaudador'] },
    { id: 'recargas-sd', name: 'Recargas SD', icon: 'credit-card', url: '/recargas-sd', roles: ['Admin', 'Supervisor'] },
    ...(isDev ? [{ id: 'recargas-plus', name: 'Recargas Plus', icon: 'credit-card', url: '/recargas-plus', roles: ['Admin'] }] : []),
    { id: 'recharges', name: 'Recargas Hoy', icon: 'credit-card', url: '/recharges', roles: ['Admin', 'Supervisor', 'Recaudador'] },
    { id: 'ca-reports', name: 'Reporte de CA', icon: 'chart-bar', url: '/ca-reports', roles: ['Admin', 'Supervisor', 'Recaudador'] },
    { id: 'reports', name: 'Reportes', icon: 'chart-bar', url: '/reports', roles: ['Admin', 'Recaudador', 'Financiero'] },
    { id: 'ticket-reports', name: 'Reportes de Tickets', icon: 'document-report', url: '/ticket-reports', roles: ['Admin', 'Recaudador'] },
    ...(isDev ? [{ id: 'facturas-recargas', name: 'Facturas Recargas', icon: 'document-text', url: '/facturas-recargas', roles: ['Admin', 'Recaudador'] }] : []),
    { id: 'quejas-ciudadanas', name: 'Reportes Ciudadanos', icon: 'chat', url: '/quejas-ciudadanas', roles: ['Admin'] },
    { id: 'audit-logs', name: 'Logs de Auditoría', icon: 'clipboard-list', url: '/audit-logs', roles: ['Admin'] },
    { id: 'tickets-disponibles', name: 'Tickets Disponibles', icon: 'ticket', url: '/tickets-disponibles', roles: ['Admin', 'Auditor', 'Financiero'] },
    { id: 'tickets-consorcio', name: 'Tickets Consorcio', icon: 'ticket', url: '/tickets-consorcio', roles: ['Admin'] },
    { id: 'user-management', name: 'Gestión de Usuarios', icon: 'user-group', url: '/user-management', roles: ['Admin'] },
    { id: 'admin-monitoreo', name: 'Monitoreo CAs', icon: 'monitor', url: '/admin-monitoreo', roles: ['Admin'] },
    { id: 'token-validation', name: 'Validación de Token SD', icon: 'shield-check', url: '/token-validation', roles: ['Admin'] },
    { id: 'add-ca', name: 'Agregar CA', icon: 'plus', url: '/add-ca', roles: ['Admin', 'Recaudador'] },
    { id: 'ca-list', name: 'Lista de CAs', icon: 'users', url: '/ca-list', roles: ['Admin', 'Recaudador', 'CA'] }
  ];

  // Función para manejar click en el Dashboard
  const handleSectionClick = (section) => {
    setActiveSection(section.id);
    navigate(section.url);

    // Si es el dashboard y estamos en desktop, colapsar/expandir el sidebar
    if (section.id === 'dashboard' && window.innerWidth >= 768) {
      setCollapsed(!collapsed);
    }

    // En móvil, cerrar el menú al hacer clic
    if (setIsMobileMenuOpen && window.innerWidth < 768) {
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      {/* Overlay para móvil */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <div
        className={`
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} 
          md:translate-x-0 
          fixed md:relative 
          inset-y-0 left-0 z-50 md:z-20
          ${collapsed ? 'md:w-16' : 'md:w-64'} 
          w-64
          bg-blue-800 text-white 
          flex flex-col 
          transition-transform duration-300 ease-in-out
        `}
      >
        {/* Header de la barra lateral */}      <div className="flex justify-between items-center p-4 border-b border-blue-700">
          {(!collapsed || isMobileMenuOpen) && (
            <h1 className="text-2xl font-semibold">Panel Admin</h1>
          )}

          {/* Botón para colapsar (sólo escritorio) */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={`p-1 rounded-full bg-blue-700 hover:bg-blue-600 ${collapsed ? 'mx-auto' : ''} hidden md:block`}
            title={collapsed ? "Expandir menú" : "Minimizar menú"}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {collapsed ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 5l7 7-7 7M5 5l7 7-7 7"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                />
              )}
            </svg>
          </button>

          {/* Botón para cerrar el menú (sólo móvil) */}
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="md:hidden p-2 text-white hover:bg-blue-700 rounded"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Secciones del menú */}
        <nav className="flex-1 overflow-y-auto">
          <ul className="py-2">
            {sections.map((section) => {
              // Verificar si el usuario tiene permiso para ver esta sección
              const hasPermission = !section.roles ||
                section.roles.includes(userRole);

              if (!hasPermission) return null;

              return (
                <li key={section.id} className="px-2">
                  <button className={`w-full flex items-center px-3 py-3 text-left rounded-lg transition-all duration-300 ${activeSection === section.id
                    ? 'bg-blue-700 text-white'
                    : 'text-blue-100 hover:bg-blue-700 hover:text-white'
                    }`}
                    onClick={() => handleSectionClick(section)}
                    title={collapsed ? section.name : ""}
                  >
                    <span className={`${collapsed && !isMobileMenuOpen ? 'mx-auto' : 'mr-3'}`}>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        {section.icon === 'search' && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        )}
                        {section.icon === 'home' && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        )}
                        {section.icon === 'plus-circle' && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                        )}
                        {section.icon === 'users' && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        )}
                        {section.icon === 'credit-card' && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        )}
                        {section.icon === 'chart-bar' && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        )}
                        {section.icon === 'clipboard-list' && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                        )}
                        {section.icon === 'user-group' && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        )}
                        {section.icon === 'plus' && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        )}
                        {section.icon === 'ticket' && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 11-2-4V7a2 2 0 00-2-2H5z" />
                        )}
                        {section.icon === 'document-text' && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        )}
                        {section.icon === 'document-report' && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        )}
                        {section.icon === 'monitor' && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        )}
                        {section.icon === 'map' && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                        )}
                        {section.icon === 'shield-check' && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        )}
                        {section.icon === 'chat' && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        )}
                      </svg>                  </span>
                    {(!collapsed || isMobileMenuOpen) && <span className="font-medium">{section.name}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
        {/* Footer con información del usuario */}
        <div className="p-4 border-t border-blue-700">
          <div className="text-sm text-blue-200">
            {(!collapsed || isMobileMenuOpen) && userRole && (
              <p className="truncate">Rol: <span className="font-semibold">{userRole}</span></p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default Sidebar;
